import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, useTheme, useMediaQuery, ThemeProvider } from '@mui/material';
import Validate from '../../../../app/validation/validation';
import { TextInputHot, MODE } from 'components/common/TextInputHot';
import { ButtonHot } from 'components/common/ButtonHot';
import { TextHot } from 'components/common/TextHot';
import { useIsMemberFirstLoginMutation } from '../../../../redux/api/users.api';
import { executeWithCaptcha, generateTestId } from '../../../../app/functions';
import { setFirstLoginReturn, setLoginCode } from '../../../../redux/actions';
import Recaptcha from '../../../../components/services/google/recaptcha';
import { RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';
import { appTheme } from 'common/theme';
import AnalyticsManager from 'app/analytics';

const FirstLoginForm = ({ onLogin, subtitleText, text, deviceState, stepUp, firstLoginValidationFailed, firstLoginValidationFailedMsg, setFirstLoginValidationFailedMsg, setFirstLoginValidationFailed, testID = 'first-login-form' }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
    const loginId = useSelector((state) => state.data.userLoginId);
    const userData = useSelector((state) => state.config.UserData);
    const firstLogin = useSelector((state) => state.config.FirstLoginReturn);
    const [isMemberFirstLoginMutation, { isLoading: isMutationLoading }] = useIsMemberFirstLoginMutation();
    const lastDigitsInputRef = useRef(null);
 
    const [isLoading, setIsLoading] = useState(false);
    const [firstTry, setFirstTry] = useState(true);
    const [form, setForm] = useState({
        id: {
            val: loginId,
            valid: false,
            rules: ['not_empty', 'id', 'not_empty'],
            errMsg: '',
            placeholder: text?.['login_id_placeholder'] || 'הזן מספר זהות',
            type: 'number',
            pattern: '[0-9]*'
        },
        last_digits: {
            val: '',
            valid: false,
            rules: ['not_empty', 'last_digits', 'not_empty'],
            errMsg: '',
            placeholder: text?.['login_last_digit_placeholder'] || '4 ספרות אחרונות',
            type: 'number',
            pattern: '[0-9]*'
        }
    });

    const handleInputChange = (evt) => {
        const { name, value } = evt.target;
        let validate = Validate(value, form[name].rules);
        let is_valid = validate.valid;
        let msg = validate.msg;
        setForm((prevForm) => ({
            ...prevForm,
            [name]: { ...prevForm[name], val: value, valid: is_valid, errMsg: msg }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading || isMutationLoading) {
            return;
        }
        
        let form_valid = true;
        let new_form = { ...form };

        for (let field in form) {
            let validate = Validate(form[field].val, form[field].rules);
            let is_valid = validate.valid;
            let msg = validate.msg;

            new_form[field].valid = is_valid;
            new_form[field].errMsg = msg;

            if (is_valid === false) {
                form_valid = false;
            }
        }

        setForm(new_form);
        setFirstTry(false);

        if (form_valid) {
            
            // Check if user already exists
            const hasExistingUserData = userData || firstLogin?.userData;
            
            if (hasExistingUserData) {
                // For existing users, just move to registration form without API call
                if (stepUp) {
                    stepUp({ before: 1, after: 2, processType: 'Register' });
                }
            } else {
                setIsLoading(true);
                executeWithCaptcha(async (token) => {
                    let payload = {};
                    for (let key in form) {
                        payload[key] = form[key].val;
                    }
                    payload['g-recaptcha-response'] = token;
                    payload['userId'] = String(form.id.val);
                    payload['last4Digits'] = String(form.last_digits.val);
                    
                    try {
                        const result = await isMemberFirstLoginMutation(payload).unwrap();
                        
                        dispatch(setFirstLoginReturn({...result, last4Digits: form.last_digits.val}));
                        dispatch(setLoginCode(0));
                        
                        // Trigger step progression to registration form
                        if (stepUp) {
                            stepUp({ before: 1, after: 2, processType: 'Register' });
                        }
                    } catch (error) {
                        setFirstLoginValidationFailed(true);
                        setFirstLoginValidationFailedMsg(error.data);
                        AnalyticsManager.logRegisterFailed();
                    } finally {
                        setIsLoading(false);
                    }
                }, RECAPTCHA_ACTIONS.VALIDATE_USER);
            }
        }
    };

    useEffect(() => {
        setIsLoading(false);
    }, [isMutationLoading]);

    useEffect(() => {
        if (lastDigitsInputRef.current) {
            const inputElement = lastDigitsInputRef.current.querySelector('input');
            if (inputElement) {
                inputElement.focus();
            }
        }
    }, []);

    const id = form.id;
    const last_digits = form.last_digits;
    const idHasError = !firstTry && !id.valid;
    const lastDigitsHasError = !firstTry && !last_digits.valid;

    return (
        <ThemeProvider theme={appTheme}>
            <Box 
                component="form" 
                onSubmit={handleSubmit} 
                testID={generateTestId(testID, 'form')} 
                sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <TextInputHot
                    name="id"
                    mode={MODE.FORM}
                    label={text?.['login_id_label'] || 'תעודת זהות'}
                    placeholder={text?.['login_id_placeholder'] || 'הזן מספר זהות'}
                    value={id.val}
                    onChange={handleInputChange}
                    error={idHasError}
                    disabled={!!id.val}
                    helperText={idHasError ? id.errMsg : ''}
                    testID={generateTestId(testID, 'id-input')}
                />

                <TextInputHot
                    ref={lastDigitsInputRef}
                    name="last_digits"
                    mode={MODE.FORM}
                    label={text?.['login_last_digits_label'] || '4 ספרות אחרונות של כרטיס האשראי'}
                    placeholder={text?.['login_last_digit_placeholder'] || 'הזן 4 ספרות'}
                    value={last_digits.val}
                    onChange={handleInputChange}
                    error={lastDigitsHasError}
                    helperText={lastDigitsHasError ? last_digits.errMsg : ''}
                    testID={generateTestId(testID, 'last-digits-input')}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <ButtonHot
                        type="submit"
                        mode="contained"
                        size={isMobile ? 'l' : 'xl'}
                        disabled={isLoading || isMutationLoading}
                        loading={isLoading || isMutationLoading}
                        testID={generateTestId(testID, 'submit-button')}
                    >
                        {text?.['registration_form_btn'] || 'חברו'}
                    </ButtonHot>
                </Box>

                {firstLoginValidationFailed && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <TextHot 
                            size="m"
                            color="error"
                            testID={generateTestId(testID, 'error-message')}
                            sx={{ textAlign: 'center' }}
                        >
                            {firstLoginValidationFailedMsg}
                        </TextHot>
                    </Box>
                )}

                <Box sx={{ mt: 2 }} testID={generateTestId(testID, 'recaptcha-container')}>
                    <Recaptcha isFunction={true} enterprise={true} onReady={() => {}} />
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default FirstLoginForm;
